/**
 * Parsers for developer-mode plan messages.
 *
 * The backend now ships each plan-related assistant message as a JSON envelope
 * in `message.content`:
 *
 *   { kind: "developer_plan",        plan_id, state, plan_markdown, presentation_html, ... }
 *   { kind: "developer_plan_state",  plan_id, state, headline?, body_markdown?, instructions? }
 *   { kind: "developer_plan_result", plan_id, state, summary_markdown, result }
 *
 * Older messages emitted the legacy `<!--8080-dev-plan:UUID-->` marker; that
 * format is gone. If we ever see message content that isn't JSON we treat it
 * as plain markdown (the normal AI reply path).
 */

export type DeveloperPlanState =
  | 'pending_approval'
  | 'approved'
  | 'denied'
  | 'revision_requested'
  | 'implementing'
  | 'implemented'
  | 'completed_no_changes'
  | string;

export interface DeveloperPlanEnvelope {
  kind: 'developer_plan';
  version: number;
  plan_id: string;
  state: DeveloperPlanState;
  headline?: string;
  plan_markdown: string;
  presentation_html: string;
  diagrams_rendered?: string[];
  diagrams_unavailable?: string[];
}

export interface DeveloperPlanStateEnvelope {
  kind: 'developer_plan_state';
  version: number;
  plan_id: string;
  state: DeveloperPlanState;
  headline?: string;
  body_markdown?: string;
  instructions?: string;
}

export interface DeveloperPlanResultEnvelope {
  kind: 'developer_plan_result';
  version: number;
  plan_id: string;
  state: DeveloperPlanState;
  headline?: string;
  summary_markdown: string;
  result: {
    plan_id: string;
    committed: boolean;
    truncated: boolean;
    files: Array<{ path: string; added?: number; deleted?: number; is_deleted?: boolean }>;
    work_branch?: string | null;
    base_branch?: string | null;
    merged_to_base?: boolean;
  };
}

export type DeveloperPlanMessage =
  | DeveloperPlanEnvelope
  | DeveloperPlanStateEnvelope
  | DeveloperPlanResultEnvelope;

/**
 * Best-effort JSON parse of a chat message content string. Returns the
 * envelope only if it advertises one of the developer-plan kinds with a
 * plan_id; otherwise returns undefined and the caller treats the message
 * as ordinary markdown.
 */
// eslint-disable-next-line max-statements
export function parsePlanEnvelope(content: string): DeveloperPlanMessage | undefined {
  const trimmed = content.trim();

  if (!trimmed.startsWith('{')) return undefined;
  try {
    const parsed = JSON.parse(trimmed);

    if (!parsed || typeof parsed !== 'object') return undefined;
    const kind = parsed.kind;

    if (kind === 'developer_plan'
        || kind === 'developer_plan_state'
        || kind === 'developer_plan_result') {
      return typeof parsed.plan_id === 'string' && parsed.plan_id ? parsed : undefined;
    }
  } catch {
    /* not JSON — fall through */
  }

  return undefined;
}

/**
 * Walk every message and return the latest known state for each plan id.
 * Each plan-related envelope (plan, state, result) carries a state field;
 * the last occurrence in chat order wins.
 */
export function collectDeveloperPlanStates(
  messages: ReadonlyArray<{ content: string }>,
): Map<string, DeveloperPlanState> {
  const states = new Map<string, DeveloperPlanState>();

  for (const message of messages) {
    const env = parsePlanEnvelope(message.content);

    if (env) states.set(env.plan_id, env.state);
  }

  return states;
}

/**
 * Visible-bubble content for a plan-related message. Plan and result
 * envelopes render their markdown body; state envelopes render the headline
 * (with the optional revision instructions appended).
 */
// eslint-disable-next-line max-statements
export function planEnvelopeVisibleMarkdown(env: DeveloperPlanMessage): string {
  if (env.kind === 'developer_plan') {
    const headline = env.headline ? `${env.headline}\n\n` : '';

    return `${headline}${env.plan_markdown}`;
  }
  if (env.kind === 'developer_plan_result') {
    return env.summary_markdown;
  }
  // state envelope
  const parts: string[] = [];

  if (env.headline) parts.push(env.headline);
  if (env.instructions) parts.push(`\n${env.instructions}`);

  return parts.join('\n');
}
